import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { v4 as uuid } from 'uuid';

import { Space, Button, Select, Input, Form, Modal, InputNumber, Flex, Tooltip, Divider, notification } from 'antd';
import { PlusOutlined, QuestionCircleOutlined, DeleteOutlined } from '@ant-design/icons';

import FormHeader from '@controls/form-header/form-header';

import TradingPlatform from '@controls/trading-platform/trading-platform';
import Counterparty from '@controls/counterparty/counterparty';

import { exception, securityRestriction, warning, error } from '@extensions/notification';

import { useAppDispatch, useAppSelector } from '@store/hooks';
import { userLoaded } from '@store/actions';
import { serverFetch } from '@flp/server';

import { IUserSession } from '@entities/user-session';
import { IConsignee } from '@entities/consignee';
import { IConsignment } from '@entities/consignment';
import { IBoxGroup } from '@entities/box-group';
import { ITradingPlatform } from '@entities/trading-platform';
import { ICounterparty } from '@entities/counterparty';
import { ICountryItem } from '@entities/country-item';
import { ICountry } from '@entities/country';
import { ICompany } from '@entities/company';
import { IPackageTemplate } from '@entities/package-template';

import { Permission, hasPermission } from '@enums/permission';
import { CompanyType } from '@enums/company-type';

import { HoldIcon } from '@core/icons';

const dayjs = require('dayjs');
var utc = require('dayjs/plugin/utc');
dayjs.extend(utc);

interface IOnHoldParams {
    onHoldReason?: string;
}

const Consignment = () => {
    const [modal, modalContextHolder] = Modal.useModal();

    const navigate = useNavigate();

    const [onHoldForm] = Form.useForm();

    const { TextArea } = Input;
    const [api, contextHolder] = notification.useNotification();

    const { truckId, consignmentId } = useParams();

    const [form] = Form.useForm();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [countryItems, setCountryItems] = useState<Array<ICountryItem>>([]);
    const [consignees, setConsignees] = useState<Array<IConsignee>>([]);
    const [counterparties, setCounterparties] = useState<Array<ICounterparty>>([]);
    const [packages, setPackages] = useState<Array<IPackageTemplate>>([]);
    const [companies, setCompanies] = useState<Array<ICompany>>([]);

    const [boxGroups, setBoxGroups] = useState<Array<IBoxGroup>>([]);

    const [currentBoxGroup, setCurrentBoxGroup] = useState<IBoxGroup>();

    const [entity, setEntity] = useState<IConsignment>({ id: undefined });

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [onHoldOpen, setOnHoldOpen] = useState<boolean>(false);

    const [manageTruckConsignment] = useState<boolean>(hasPermission(userSession.permissions, Permission.ManageTruckConsignment));

    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [onHoldProcessing, setOnHoldProcessing] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('countries', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('countries/items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`tradingplatforms`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения торговых площадок', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('consignees', { method: 'GET', queryParams: { isArchived: false } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения грузополучателей', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('packagetemplates', { method: 'GET', queryParams: { isArchived: false } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения упаковок', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('companies', { method: 'GET', queryParams: { type: CompanyType.Remittance } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения компаний', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (consignmentId) {
                promises.push(
                    await serverFetch(`consignments/${consignmentId}/import`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                let loadedCountries: Array<ICountry> = result[0][0];
                setCountryItems(result[0][1]);

                let serverPlatforms: Array<ITradingPlatform> = result[0][2];

                setConsignees(result[0][3]);
                setPackages(result[0][4]);
                setCompanies(result[0][5]);

                let data: IConsignment = result[0][6] || { ...entity, truckId: truckId };

                loadedCountries.map((c: ICountry) => {
                    let tmpPlatforms = serverPlatforms.filter((sp) => sp.countryId == c.id);
                    c.platforms = [...tmpPlatforms];

                    let countryBoxGroups = data.boxGroups?.filter((b) => b.countryId === c.id);

                    if (countryBoxGroups && countryBoxGroups.length > 0) {
                        countryBoxGroups?.map((bg) => {
                            form.setFieldsValue({
                                cityId: bg.cityId,
                            });
                        });
                    }
                });

                setCountries(loadedCountries);

                if (consignmentId) {
                    form.setFieldsValue({
                        id: consignmentId,
                        cityId: data.cityId,
                        consigneeId: data.consigneeId,
                        counterpartyId: data.counterpartyId,
                    });

                    data.boxGroups?.map((b) => (b.key = b.id));

                    setBoxGroups(data.boxGroups || []);
                    loadCounterparties(data.userId);
                }

                setEntity({ ...data });

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    const loadCounterparties = (userId?: string) => {
        if (!userId) return;

        serverFetch(`counterparties`, { method: 'GET', queryParams: { userId: userId } })
            .then((data) => {
                setCounterparties(data);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения контрагентов', ex, () => d(userLoaded(undefined)));
            });
    };

    const loadPlatforms = () => {
        serverFetch(`tradingplatforms`, { method: 'GET' })
            .then((data) => {
                let tmpCountries = [...countries];

                tmpCountries.map((c: ICountry) => {
                    let tmpPlatforms = data.filter((d: ITradingPlatform) => d.countryId == c.id);
                    c.platforms = [...tmpPlatforms];
                });

                setCountries(tmpCountries);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения торговых площадок', ex, () => d(userLoaded(undefined)));
            });
    };

    const onСonsigneeChanged = async (value: string | undefined) => {
        let consignee = consignees.find((m) => m.id == value);

        setEntity({
            ...entity,
            counterpartyId: undefined,
            legalName: undefined,
            userId: consignee?.userId,
        });

        if (!consignee) {
            form.setFieldValue('counterpartyId', undefined);
            return;
        }

        form.setFieldsValue({
            consigneeId: value,
            cityId: consignee.cityId,
            userId: consignee.userId,
        });

        loadCounterparties(consignee.userId);
    };

    const onFinish = (data: IConsignment) => {
        let filledBoxGroups = boxGroups.filter((bg) => bg.qty && bg.qty > 0);
        if (filledBoxGroups.length <= 0) {
            warning(api, 'Хотя бы одна партия должна быть заполнена');
            return;
        }

        const duplicate = filledBoxGroups.find((bg) => {
            const duplicate = filledBoxGroups.find(
                (d) =>
                    d.key != bg.key &&
                    d.countryId == bg.countryId &&
                    d.itemId == bg.itemId &&
                    d.palletQty == bg.palletQty &&
                    d.packageTemplateId == bg.packageTemplateId &&
                    d.qty == bg.qty &&
                    d.volume == bg.volume &&
                    d.tradingPlatform == bg.tradingPlatform &&
                    d.companyId == bg.companyId &&
                    d.referenceNumber == bg.referenceNumber,
            );
            return !!duplicate;
        });

        if (duplicate) {
            error(api, `Удалите дублирующиеся позиции "${duplicate.countryName}"`);
            return;
        }

        filledBoxGroups.map((bg) => {
            bg.cityId = data.cityId;
        });

        let result = { ...entity, ...data, boxGroups: filledBoxGroups };

        setSaving(true);

        serverFetch(`consignments`, { method: consignmentId ? 'PUT' : 'POST', bodyData: result })
            .then(() => {
                setSaving(false);
                navigate(-1);
            })
            .catch((ex) => {
                setSaving(false);
                exception(api, 'Ошибка сохранения груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSetOnHold = (entity: IOnHoldParams) => {
        if (!currentBoxGroup) return;

        setOnHoldProcessing(true);

        serverFetch(`remainings/onhold`, {
            method: 'POST',
            bodyData: {
                ids: [currentBoxGroup.id],
                onHoldReason: entity.onHoldReason,
            },
        })
            .then(() => {
                setOnHoldProcessing(false);

                onHoldForm.resetFields();
                setCurrentBoxGroup(undefined);
                setOnHoldOpen(false);

                setRefreshRequired(true);
            })
            .catch((ex) => {
                setOnHoldProcessing(false);
                exception(api, 'Ошибка блокировки грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };

    const onAddBoxGroup = () => {
        let groups = [...boxGroups];

        groups.push({
            key: uuid(),
            id: undefined,
            consignmentId: consignmentId,
            countryId: undefined,
            countryName: undefined,
            countryCode: undefined,
            itemId: undefined,
            qty: undefined,
            calcVolume: undefined,
            loadingOn: undefined,
            awbNumber: undefined,
        });

        setBoxGroups(groups);
    };

    const onDeleteBoxGroup = (key: string | undefined) => {
        if (!key) return;

        let groups = [...boxGroups];
        groups = groups.filter((g) => g.key != key);

        setBoxGroups(groups);
    };

    const renderBoxGroupsForm = () => {
        let bgCopy = [...boxGroups];
        return bgCopy.map((bg: IBoxGroup) => {
            return (
                <Form.Item key={bg.key} labelCol={{ span: 2 }} wrapperCol={{ span: 22 }}>
                    <Flex gap='small' align='flex-start'>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                icon={<DeleteOutlined />}
                                disabled={bg.hasBill}
                                onClick={() => {
                                    modal.confirm({
                                        title: `Удалить выбранную партию?`,
                                        icon: <QuestionCircleOutlined />,
                                        okType: 'primary',
                                        okText: 'ОК',
                                        cancelText: 'Отмена',
                                        onOk: () => {
                                            onDeleteBoxGroup(bg.key);
                                        },
                                    });
                                }}
                            />
                        </Form.Item>

                        <Flex gap='small' align='center' wrap>
                            <Form.Item
                                name={`${bg.key}countryId`}
                                initialValue={bg.countryId}
                                style={{ width: 150, minWidth: 150, marginBottom: 0 }}
                                rules={[{ required: true, message: 'Укажите страну' }]}
                            >
                                <Select
                                    placeholder='Страна'
                                    disabled={!manageTruckConsignment}
                                    onChange={(value) => {
                                        let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                        if (boxGroup) {
                                            boxGroup.countryId = value;
                                            boxGroup.country = countries.find((c) => c.id == value);

                                            const items = countryItems.filter((i) => i.countryId == value);
                                            const item = items.find((i) => i.itemId == boxGroup.itemId);

                                            if (!item) {
                                                boxGroup.itemId = undefined;
                                                form.setFieldValue(`${bg.key}itemId`, undefined);
                                            }
                                        }
                                        setBoxGroups(bgCopy);
                                    }}
                                    showSearch={{
                                        filterOption: (input, option) =>
                                            (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                    }}
                                    options={countries.map((i) => {
                                        return { value: i.id, label: i.name };
                                    })}
                                />
                            </Form.Item>
                            <Form.Item
                                name={`${bg.key}itemId`}
                                initialValue={bg.itemId}
                                style={{ width: 150, minWidth: 150, marginBottom: 0 }}
                                rules={[{ required: true, message: 'Укажите продукт' }]}
                            >
                                <Select
                                    placeholder='Продукт'
                                    disabled={!manageTruckConsignment}
                                    onChange={(value) => {
                                        let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                        if (boxGroup) {
                                            boxGroup.itemId = value;
                                        }
                                        setBoxGroups(bgCopy);
                                    }}
                                    showSearch={{
                                        filterOption: (input, option) =>
                                            (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                    }}
                                    options={countryItems
                                        .filter((i) => i.countryId == bg.countryId)
                                        .map((i) => {
                                            return { value: i.itemId, label: i.itemName };
                                        })}
                                />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space.Compact>
                                    <Form.Item initialValue={bg.palletQty} name={`${bg.key}palletQty`} noStyle>
                                        <InputNumber
                                            style={{ width: 100, minWidth: 100 }}
                                            placeholder='Палеты'
                                            disabled={!manageTruckConsignment}
                                            precision={3}
                                            decimalSeparator=','
                                            min={0}
                                            onChange={(value: number | null) => {
                                                let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                                if (boxGroup) {
                                                    boxGroup.palletQty = value;
                                                }
                                                setBoxGroups(bgCopy);
                                            }}
                                        />
                                    </Form.Item>
                                    <Space.Addon>шт</Space.Addon>
                                </Space.Compact>
                            </Form.Item>
                            <Form.Item
                                name={`${bg.key}packageTemplateId`}
                                initialValue={bg.packageTemplateId}
                                style={{ width: 180, minWidth: 180, marginBottom: 0 }}
                                rules={[{ required: true, message: 'Укажите упаковку' }]}
                            >
                                <Select
                                    placeholder='Упаковка / тип'
                                    disabled={!manageTruckConsignment}
                                    onChange={(value) => {
                                        let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                        if (boxGroup) {
                                            boxGroup.packageTemplateId = value;

                                            const currentPackage = packages.find((p) => p.id == value);
                                            if (currentPackage && currentPackage.qtyPerPallet && boxGroup.qty) {
                                                const palletQty = boxGroup.qty / currentPackage.qtyPerPallet;
                                                boxGroup.palletQty = palletQty;
                                                form.setFieldValue(`${bg.key}palletQty`, palletQty);
                                            }
                                        }
                                        setBoxGroups(bgCopy);
                                    }}
                                    showSearch={{
                                        filterOption: (input, option) =>
                                            (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                    }}
                                    options={packages.map((i) => {
                                        return { value: i.id, label: i.name };
                                    })}
                                />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space.Compact>
                                    <Form.Item initialValue={bg.qty} name={`${bg.key}qty`} noStyle>
                                        <InputNumber
                                            style={{ width: 100, minWidth: 100 }}
                                            placeholder='Кол-во'
                                            disabled={!manageTruckConsignment}
                                            min={0}
                                            onChange={(value: number | null) => {
                                                let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                                if (boxGroup) {
                                                    boxGroup.qty = value;

                                                    const currentPackage = packages.find((p) => p.id == boxGroup.packageTemplateId);
                                                    if (currentPackage && currentPackage.qtyPerPallet && boxGroup.qty) {
                                                        const palletQty = boxGroup.qty / currentPackage.qtyPerPallet;
                                                        boxGroup.palletQty = palletQty;
                                                        form.setFieldValue(`${bg.key}palletQty`, palletQty);
                                                    }
                                                }
                                                setBoxGroups(bgCopy);
                                            }}
                                        />
                                    </Form.Item>
                                    <Space.Addon>шт</Space.Addon>
                                </Space.Compact>
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space.Compact>
                                    <Form.Item initialValue={bg.volume} name={`${bg.key}volume`} noStyle>
                                        <InputNumber
                                            style={{ width: 100, minWidth: 100 }}
                                            decimalSeparator=','
                                            precision={1}
                                            placeholder='Объем'
                                            min={0}
                                            disabled={!manageTruckConsignment}
                                            stringMode
                                            step={0.01}
                                            onChange={(value: number | null) => {
                                                let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                                if (boxGroup) {
                                                    boxGroup.volume = value;
                                                }
                                                setBoxGroups(bgCopy);
                                            }}
                                        />
                                    </Form.Item>
                                    <Space.Addon>
                                        м<sup>3</sup>
                                    </Space.Addon>
                                </Space.Compact>
                            </Form.Item>

                            <Form.Item
                                name={`${bg.key}tradingPlatform`}
                                initialValue={bg.tradingPlatform}
                                style={{ width: 250, minWidth: 250, marginBottom: 0 }}
                            >
                                <TradingPlatform
                                    country={bg.country}
                                    basePlatforms={bg.country?.platforms || []}
                                    defaultPlatformName={bg.tradingPlatform}
                                    disabled={!manageTruckConsignment || !bg.countryId}
                                    api={api}
                                    onSelect={(value: string) => {
                                        let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                        if (boxGroup) {
                                            boxGroup.tradingPlatform = value;
                                        }

                                        setBoxGroups(bgCopy);
                                    }}
                                    loadPlatforms={loadPlatforms}
                                    serverFetch={serverFetch}
                                />
                            </Form.Item>

                            <Form.Item
                                name={`${bg.key}companyId`}
                                initialValue={bg.companyId}
                                style={{ width: 250, minWidth: 250, marginBottom: 0 }}
                                rules={[{ required: true, message: 'Укажите плательщика' }]}
                            >
                                <Select
                                    placeholder='Плательщик'
                                    disabled={!manageTruckConsignment}
                                    onChange={(value) => {
                                        let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                        if (boxGroup) {
                                            boxGroup.companyId = value;
                                            boxGroup.country = countries.find((c) => c.id == value);
                                        }
                                        setBoxGroups(bgCopy);
                                    }}
                                    showSearch={{
                                        filterOption: (input, option) =>
                                            (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                    }}
                                    options={companies.map((i) => {
                                        return { value: i.id, label: i.legalName };
                                    })}
                                />
                            </Form.Item>

                            <Form.Item
                                name={`${bg.key}referenceNumber`}
                                initialValue={bg.referenceNumber}
                                style={{ width: 150, minWidth: 150, marginBottom: 0 }}
                            >
                                <Input
                                    placeholder='Номер заказа'
                                    disabled={!manageTruckConsignment}
                                    onChange={(data) => {
                                        let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                        if (boxGroup) {
                                            boxGroup.referenceNumber = data.target.value;
                                        }

                                        setBoxGroups(bgCopy);
                                    }}
                                />
                            </Form.Item>

                            <Form.Item
                                name={`${bg.key}comment`}
                                initialValue={bg.comment}
                                style={{ width: 300, minWidth: 300, marginBottom: 0 }}
                            >
                                <Input
                                    placeholder='Комментарий'
                                    disabled={!manageTruckConsignment}
                                    onChange={(data) => {
                                        let boxGroup = bgCopy.find((d) => d.key === bg.key);
                                        if (boxGroup) {
                                            boxGroup.comment = data.target.value;
                                        }

                                        setBoxGroups(bgCopy);
                                    }}
                                />
                            </Form.Item>

                            {bg.id && (
                                <Tooltip title={bg.onHold ? 'Разблокировать' : 'Заблокировать'}>
                                    <Button
                                        style={{ width: 35, minWidth: 35, color: bg.onHold ? 'darkorchid' : '' }}
                                        icon={<HoldIcon />}
                                        onClick={() => {
                                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                                securityRestriction(api, [Permission.ManageWarehouse]);
                                                return;
                                            }

                                            setCurrentBoxGroup(bg);

                                            setOnHoldOpen(true);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Flex>
                    </Flex>
                </Form.Item>
            );
        });
    };

    return (
        <>
            <FormHeader title={`${consignmentId ? 'Изменить партию' : 'Добавить партию'}`} />
            {!loading && (
                <Form colon={false} labelCol={{ span: 3 }} wrapperCol={{ span: 16 }} form={form} onFinish={onFinish}>
                    <Form.Item initialValue={entity.id} name='id' hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item initialValue={entity.cityId} name='cityId' hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{ span: 8 }}
                        initialValue={entity.consigneeId}
                        label='Клиент'
                        name='consigneeId'
                        rules={[{ required: true, message: 'Выберите клиента' }]}
                    >
                        <Select
                            allowClear
                            disabled={!manageTruckConsignment}
                            autoFocus
                            onChange={(value) => onСonsigneeChanged(value)}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                            }}
                            options={consignees.map((с) => {
                                let label = с.keyName
                                    ? `${с.code} / ${с.keyName} / ${с.fullName} / ${с.cityName}`
                                    : `${с.code} / ${с.fullName} / ${с.cityName}`;
                                return { value: с.id, label: label };
                            })}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ span: 8 }} label='Контрагент' initialValue={entity.counterpartyId} name='counterpartyId'>
                        <Counterparty
                            userId={entity.userId}
                            legalName={entity.legalName}
                            baseCounterparties={counterparties}
                            disabled={!manageTruckConsignment}
                            api={api}
                            onSelect={(value: ICounterparty | undefined) => {
                                setEntity({
                                    ...entity,
                                    counterpartyId: value?.id,
                                    legalName: value?.legalName,
                                });
                                form.setFieldValue('counterpartyId', value?.id);
                            }}
                            loadCounterparties={() => loadCounterparties(entity.userId)}
                            serverFetch={serverFetch}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ span: 20 }} initialValue={entity.comment} label='Комментарий' name='comment'>
                        <TextArea
                            rows={3}
                            disabled={!manageTruckConsignment}
                            onChange={(data) => {
                                setEntity({ ...entity, comment: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 1, span: 22 }}>
                        <Divider titlePlacement='left' style={{ marginBottom: 0 }}>
                            <Tooltip title='Добавить' placement='topLeft'>
                                <Button
                                    size='small'
                                    icon={<PlusOutlined />}
                                    style={{ margin: '4px 15px 2px -5px' }}
                                    onClick={() => {
                                        onAddBoxGroup();
                                    }}
                                />
                            </Tooltip>
                        </Divider>
                    </Form.Item>
                    <Form.Item style={{ marginTop: 0, marginBottom: 0 }} wrapperCol={{ offset: 1, span: 22 }}>
                        {renderBoxGroupsForm()}
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 1, span: 22 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Закрыть
                            </Button>
                            {manageTruckConsignment && (
                                <Button type='primary' htmlType='submit' loading={loading || saving}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            <Modal
                title={
                    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'start' }}>
                        <QuestionCircleOutlined style={{ color: '#faad14', marginInlineEnd: 12, fontSize: 22 }} />
                        {currentBoxGroup?.onHold
                            ? `Разблокировать '${currentBoxGroup.countryName}'?`
                            : `Заблокировать '${currentBoxGroup?.countryName}'?`}
                    </div>
                }
                open={onHoldOpen}
                okText='Сохранить'
                onOk={() => onHoldForm.submit()}
                onCancel={() => {
                    onHoldForm.resetFields();
                    setCurrentBoxGroup(undefined);
                    setOnHoldOpen(false);
                }}
                closable={false}
                cancelButtonProps={{ style: { display: onHoldProcessing ? 'none' : '' } }}
                confirmLoading={onHoldProcessing}
            >
                {!currentBoxGroup?.onHold && (
                    <Form
                        colon={false}
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 18 }}
                        onFinish={onSetOnHold}
                        form={onHoldForm}
                        style={{ marginTop: 10 }}
                    >
                        <Form.Item
                            required={true}
                            label='Причина'
                            name='onHoldReason'
                            rules={[{ required: true, message: 'Укажите причину блокировки' }]}
                        >
                            <TextArea autoFocus rows={4} />
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Consignment;
